import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema } from '@/components/base/SchemaOrg';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
          <Navbar />
          <main className="pt-24 pb-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
              <i className="ri-error-warning-line text-3xl" style={{ color: 'oklch(var(--foreground-300))' }}></i>
            </div>
            <h1 className="text-xl font-bold mb-2" style={{ color: 'oklch(var(--foreground-800))' }}>影片未找到</h1>
            <p className="text-sm mb-6" style={{ color: 'oklch(var(--foreground-400))' }}>该影片可能已下架或链接有误</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedMovies = getRelatedMovies(movie, 6);

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        title={`${movie.title} - 黄瓜影视在线看`}
        description={movie.description}
        url={`https://huanggua.tv/article/${movie.id}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://huanggua.tv' },
          { name: movie.categoryLabel, url: `https://huanggua.tv/category/${movie.category}` },
          { name: movie.title, url: `https://huanggua.tv/article/${movie.id}` },
        ]}
      />
      <ArticleSchema
        title={movie.title}
        description={movie.description}
        image={movie.poster}
        datePublished={`${movie.year}-01-01`}
        dateModified={movie.updateInfo.replace(' 更新', '').replace('更新完', '')}
        author={movie.director}
        url={`https://huanggua.tv/article/${movie.id}`}
      />

      <div className="min-h-screen" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
        <Navbar />

        <main className="pt-20 pb-12">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors" style={{ color: 'oklch(var(--foreground-400))' }}>首页</Link>
              <span style={{ color: 'oklch(var(--foreground-300))' }}>/</span>
              <Link to={`/category/${movie.category}`} className="hover:text-primary-500 transition-colors" style={{ color: 'oklch(var(--foreground-400))' }}>{movie.categoryLabel}</Link>
              <span style={{ color: 'oklch(var(--foreground-300))' }}>/</span>
              <span className="truncate max-w-[200px]" style={{ color: 'oklch(var(--foreground-700))' }}>{movie.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px] mx-auto lg:mx-0">
                <div className="relative aspect-[2/3] rounded-xl overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500/90 text-white whitespace-nowrap">
                    {movie.categoryLabel}
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-bold bg-accent-500/90 text-white">
                    {movie.rating} 分
                  </span>
                  {movie.isNew && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-secondary-500/80 text-white">NEW</span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading mb-2" style={{ color: 'oklch(var(--foreground-950))' }}>
                  {movie.title}
                </h1>
                <p className="text-sm mb-5" style={{ color: 'oklch(var(--foreground-400))' }}>
                  {movie.titleEn}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 mb-6">
                  <InfoItem label="导演" value={movie.director} />
                  <InfoItem label="年份" value={String(movie.year)} />
                  <InfoItem label="地区" value={movie.region} />
                  <InfoItem label="类型" value={movie.genre.join(' / ')} />
                  <InfoItem label="时长" value={movie.duration} />
                  <InfoItem label="播放量" value={movie.views} />
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-semibold mb-2" style={{ color: 'oklch(var(--foreground-700))' }}>主演</h3>
                  <div className="flex flex-wrap gap-2">
                    {movie.cast.map(actor => (
                      <span
                        key={actor}
                        className="px-3 py-1 rounded-lg text-xs"
                        style={{
                          backgroundColor: 'oklch(var(--background-100))',
                          color: 'oklch(var(--foreground-500))',
                        }}
                      >
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-sm font-semibold mb-2" style={{ color: 'oklch(var(--foreground-700))' }}>剧情简介</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
                    {movie.description}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="px-8 py-3 rounded-full text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-white transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2">
                    <i className="ri-play-fill text-lg"></i>立即播放
                  </button>
                  <button className="px-8 py-3 rounded-full text-sm font-medium border transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2" style={{ borderColor: 'oklch(var(--foreground-200))', color: 'oklch(var(--foreground-600))' }}>
                    <i className="ri-heart-line text-lg"></i>收藏
                  </button>
                  <button className="px-8 py-3 rounded-full text-sm font-medium border transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2" style={{ borderColor: 'oklch(var(--foreground-200))', color: 'oklch(var(--foreground-600))' }}>
                    <i className="ri-share-line text-lg"></i>分享
                  </button>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-14 md:mt-18">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: 'oklch(var(--secondary-500)/0.12)' }}>
                    <i className="ri-movie-2-line text-base" style={{ color: 'oklch(var(--secondary-500))' }}></i>
                  </div>
                  <h2 className="text-lg md:text-xl font-bold font-heading" style={{ color: 'oklch(var(--foreground-950))' }}>
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                  {relatedMovies.map((item, index) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group cursor-pointer animate-scale-in"
                      style={{ animationDelay: `${index * 60}ms` }}
                    >
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5">
                        <img
                          src={item.poster}
                          alt={item.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute top-2 right-2">
                          <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-accent-500/90 text-white">
                            {item.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium truncate group-hover:text-primary-500 transition-colors" style={{ color: 'oklch(var(--foreground-800))' }}>
                        {item.title}
                      </h3>
                      <p className="text-xs mt-0.5" style={{ color: 'oklch(var(--foreground-400))' }}>
                        {item.year} · {item.categoryLabel}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs mb-0.5" style={{ color: 'oklch(var(--foreground-400))' }}>{label}</dt>
      <dd className="text-sm font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>{value}</dd>
    </div>
  );
}